import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { UsageChip } from "@/components/layout/UsageChip";
import { CurrencySelector } from "@/components/layout/CurrencySelector";
import { PUBLIC_LINKS, SwitchAreaLink, MobileMenu, useNavbarChrome, isAccountPath } from "./NavbarParts";
import { WorkspaceMenu, AccountMenu } from "./NavbarMenus";

export { isAccountPath };

/**
 * Two navbars in one:
 *  - Public site: marketing links; a signed-in visitor also gets one "Open my account" button.
 *  - Account area: marketing links are hidden; a single "workspace map" dropdown replaces
 *    the long row of account links, plus credits and the avatar menu (log out lives there).
 */
const Brand = ({ inAccount }) => (
  <Link to={inAccount ? "/studio" : "/"} className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="logo">
    <span className="gradient-text">LUMORA</span>
    {inAccount && <span className="hidden sm:inline text-[10px] font-bold uppercase tracking-[0.2em] text-white/35 mt-1">Studio</span>}
  </Link>
);

const DesktopNav = ({ user, inAccount }) => (
  <div className="hidden lg:flex items-center gap-7">
    {!inAccount && PUBLIC_LINKS.map((l) => (
      <a key={l.href} href={l.href} className="text-white/90 hover:text-white transition-colors text-sm font-medium whitespace-nowrap">{l.label}</a>
    ))}
    {inAccount && <WorkspaceMenu user={user} />}
    {user && !inAccount && <SwitchAreaLink inAccount={false} />}
  </div>
);

const RightCluster = ({ user, logout, inAccount, mobileOpen, toggleMobile }) => (
  <div className="flex items-center gap-3">
    {!inAccount && <CurrencySelector className="hidden md:inline-flex" />}
    {user && <UsageChip />}
    {user ? (
      <AccountMenu user={user} logout={logout} inAccount={inAccount} />
    ) : (
      <Button asChild data-testid="nav-login-btn"
        className="rounded-full px-5 md:px-6 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-semibold shadow-lg shadow-purple-500/25 hover:shadow-purple-500/50 transition-all">
        <Link to="/login">Login</Link>
      </Button>
    )}
    <button type="button" onClick={toggleMobile} aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} data-testid="mobile-menu-toggle"
      className="lg:hidden w-9 h-9 rounded-full border border-white/20 text-white/80 flex items-center justify-center hover:bg-white/10 transition-all">
      {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
    </button>
  </div>
);

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const inAccount = Boolean(user) && isAccountPath(pathname);
  const { scrolled, mobileOpen, toggleMobile, closeMobile } = useNavbarChrome();

  return (
    <>
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} transition={{ duration: 0.6 }} data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || mobileOpen || inAccount ? "glass py-4" : "py-6"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-4">
          <Brand inAccount={inAccount} />
          <DesktopNav user={user} inAccount={inAccount} />
          <RightCluster user={user} logout={logout} inAccount={inAccount} mobileOpen={mobileOpen} toggleMobile={toggleMobile} />
        </div>
      </motion.nav>
      <MobileMenu open={mobileOpen} user={user} inAccount={inAccount} onClose={closeMobile} logout={logout} />
    </>
  );
};
